import { Navigate, RouteObject } from 'react-router-dom';
import { AuthenticationCallbackWrapper } from '../Views/AuthenticationCallback';
import { DashboardPage } from '@features/dashboard';
import { RootErrorBoundary } from '../components/errors/RootErrorBoundary';
import NoteViewRevamp from '../Views/NoteViewRevamp';
import { EditNoteTemplate } from '@features/notes';
import NoteJotDocView from '../features/notes/routes/NoteJotDocView';
import UserView from '../Views/UserView';
import UserNotesView from '../Views/UserNotesView';
import { NoteProcessingView } from '../Views/NoteProcessingView';
import { AdminViewOnly } from '../Views/Admin/AdminViewOnly';
import { TranscribeView } from '../Views/Admin/TranscribeView';
import GroupNotesView from '../Views/GroupNotesView';
import ViewTemplate from '../Views/Shared/ViewTemplate';
import ViewUserTemplates from '../Views/Shared/ViewUserTemplates';
import {
  CliniciansPage,
  OrganizationManagementPage,
  PracticeSettingsPage,
  SubscriptionsPage,
} from '@features/organizations';
import { RecorderView } from '../Views/RecorderView';
import { TestNoteRecordingView } from '../Views/TestNoteRecordingView';
import { PatientsView } from '../Views/PatientsView';
import PatientDetailView from '../components/Patient/PatientDetailView';
import { ClaimSubmittedView } from '../Views/ClaimSubmittedView';
import CheckoutSuccess from '../Views/CheckoutSuccess';
import CheckoutCancel from '../Views/CheckoutCancel';
import { EncounterPage, FirstEncounterPage } from '@features/encounters';
import { ScheduleView } from '@features/appointments';
import { DocumentsPage, LabsPage, FormsPage, DocumentTemplatesPage } from '@features/documents';
import FormDetailFullView from '../Views/FormDetailFullView';
import TemplateBuilderView from '../components/TemplateBuilder/TemplateBuilderView';
import { TemplatesPage } from '@features/templates';
import OnboardingView from '../Views/OnboardingViewRevamp';
import { AuthenticationSignupWrapper } from '../Views/Admin/UserSignup';
import { AuthenticatedLayout } from './layouts/AuthenticatedLayout';
import { ActionsBarLayout } from './layouts/ActionsBarLayout';
import { Auth0ProviderWithNavigate } from '../auth0-provider';
import GoogleAuthCallback from '../Views/GoogleAuthCallback';
import { GoogleAuthCallbackWrapper } from '../Views/GoogleAuthCallbackWrapper';
import { DiagnosticsView } from '@views/DiagnosticsView';
import { RecordingPermissionGate } from '../components/guards/RecordingPermissionGate';
import { BillingLandingView } from '../Views/BillingLandingView';
import { ClaimDetailView } from '../Views/ClaimDetailView';
import { ClaimView } from '../Views/ClaimView';
import { BillInsurancePage } from '@features/billing';
import { ReportsAnalyticsPage } from '@features/analytics';
import { AuditPage, CoachPage } from '@features/jotcoach';
import { PatientCommunicationsPage } from '@features/patients';
import { BillPatientsPage } from '@features/payments';

export const mainRoutes: RouteObject[] = [
  { path: '/callback', element: <AuthenticationCallbackWrapper /> },
  {
    path: '/google-auth-callback',
    element: <GoogleAuthCallbackWrapper />,
  },
  {
    path: '/signup',
    element: <AuthenticationSignupWrapper />,
  },
  {
    path: '/share/templates/:templateId',
    element: <ViewTemplate />,
  },
  {
    path: '/share/templates/created-by/:userId',
    element: <ViewUserTemplates />,
  },
  {
    path: '/',
    element: <AuthenticatedLayout />,
    errorElement: (
      <Auth0ProviderWithNavigate>
        <RootErrorBoundary />
      </Auth0ProviderWithNavigate>
    ),
    children: [
      {
        path: '/',
        index: true,
        element: <DashboardPage />,
      },
      {
        path: '/dashboard',
        element: <DashboardPage />,
      },
      {
        path: '/templatebuilder',
        element: <TemplateBuilderView />,
      },
      // {
      //   path: '/billing',
      //   element: <ClaimSubmittedView />,
      // },
      {
        path: '/billing',
        element: <BillingLandingView />,
      },
      {
        path: '/bill-insurance',
        element: <BillInsurancePage />,
      },
      {
        path: '/reports-analytics',
        element: <ReportsAnalyticsPage />,
      },
      {
        path: '/bill-patients',
        element: <BillPatientsPage />,
      },
      {
        path: '/communications',
        element: <PatientCommunicationsPage />,
      },
      {
        path: '/jotcoach',
        element: <CoachPage />,
      },
      {
        path: '/jotaudit',
        element: <AuditPage />,
      },
      {
        path: '/billing/claim/:claimId',
        element: <ClaimView />,
      },
      {
        path: '/billing/claim/:claimId/edit',
        element: <ClaimDetailView />,
      },
      {
        path: '/patients/',
        element: <PatientsView />,
      },
      {
        path: '/patient/:patientId',
        element: <PatientDetailView />,
      },
      {
        path: '/notes/',
        element: <UserNotesView />,
      },
      {
        path: '/templates/',
        element: <TemplatesPage />,
      },
      {
        path: '/onboarding/',
        element: <OnboardingView />,
      },
      {
        path: '/record/',
        element: (
          <RecordingPermissionGate>
            <RecorderView />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/schedule',
        element: <ScheduleView />,
      },
      {
        path: '/documents',
        element: <DocumentsPage />,
      },
      {
        path: '/document-templates',
        element: <DocumentTemplatesPage />,
      },
      {
        path: '/labs',
        element: <LabsPage />,
      },
      {
        path: '/form-documents',
        element: <FormsPage />,
      },
      {
        path: '/forms',
        async lazy() {
          const { LibraryView } = await import('@features/form-library');
          return { Component: LibraryView };
        },
      },
      {
        path: '/rules',
        async lazy() {
          const { RulesManagementPage } = await import('@features/rules');
          return { Component: RulesManagementPage };
        },
      },
      {
        path: '/form-builder/:formId',
        async lazy() {
          const { BuilderPage } = await import('@features/form-builder');
          return { Component: BuilderPage };
        },
      },
      {
        path: '/encounters/:encounterId',
        element: (
          <RecordingPermissionGate>
            <EncounterPage />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/first-encounter',
        element: (
          <RecordingPermissionGate>
            <FirstEncounterPage />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/first-encounter/:encounterId',
        element: (
          <RecordingPermissionGate>
            <FirstEncounterPage />
          </RecordingPermissionGate>
        ),
      },
      {
        path: '/forms/:formSessionId',
        element: <FormDetailFullView />,
      },
      {
        path: '/exampleRecord/',
        element: <TestNoteRecordingView />,
      },
      {
        path: '/note/:noteId',
        children: [
          {
            index: true,
            async lazy() {
              const { useFeatureGate } = await import('@services/statsig');
              const NoteViewRevamp = (await import('../Views/NoteViewRevamp')).default;
              const { NoteChatView } = await import('@features/note-ai-edit');
              const { ActionsBarLayout } = await import('./layouts/ActionsBarLayout');

              // Feature gate decides whether to apply ActionsBarLayout wrapper
              const ConditionalLayoutNoteView = () => {
                const isAIEditEnabled = useFeatureGate('feature_note_ai_edit');

                if (isAIEditEnabled) {
                  // No layout wrapper - NoteChatView is standalone
                  return <NoteChatView />;
                }

                // Wrap NoteViewRevamp with ActionsBarLayout
                return (
                  <ActionsBarLayout>
                    <NoteViewRevamp />
                  </ActionsBarLayout>
                );
              };

              return { Component: ConditionalLayoutNoteView };
            },
          },
          {
            path: 'edit-template',
            element: <ActionsBarLayout />,
            children: [
              {
                index: true,
                element: <EditNoteTemplate />,
              },
            ],
          },
        ],
      },
      {
        path: '/jotdoc/:jotdocId',
        element: <ActionsBarLayout />,
        children: [
          {
            index: true,
            element: <NoteJotDocView />,
          },
        ],
      },
      {
        path: '/status/:noteId',
        element: <NoteProcessingView />,
      },
      {
        path: '/transcribe',
        element: (
          <AdminViewOnly>
            <TranscribeView />
          </AdminViewOnly>
        ),
      },
      {
        path: '/user',
        element: <UserView />,
      },
      {
        path: '/group',
        element: <GroupNotesView />,
      },
      {
        path: '/group_administration',
        element: <Navigate to="/practice/clinicians" replace />,
      },
      {
        path: '/practice/clinicians',
        element: <CliniciansPage />,
      },
      {
        path: '/practice/settings',
        element: <PracticeSettingsPage />,
      },
      {
        path: '/practice/subscriptions',
        element: <SubscriptionsPage />,
      },
      {
        path: '/checkout-success',
        element: <CheckoutSuccess />,
      },
      {
        path: '/checkout-cancel',
        element: <CheckoutCancel />,
      },
      {
        path: '/google-auth-callback',
        element: <GoogleAuthCallback />,
      },
      {
        path: '/diagnostics',
        element: <DiagnosticsView />,
      },
    ],
  },
];
